import ModalConsentForm, { consentFormRef } from 'features/cashier/components/CenterMainBody/ModalConsentForm';
import DetailsCustomerModal, { detailsCustomerModalRef } from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal';
import multiCardActions from 'features/payment/multipleCard/services/actions';
import multiCardApis from 'features/payment/multipleCard/services/apis';
import { IMultiCardResDataItem } from 'features/payment/multipleCard/services/types/card';
import paymentActions from 'features/payment/services/actions';
import paymentApis from 'features/payment/services/apis';
import { PAYMENT_TYPE } from 'features/payment/services/constants';
import { IBillDetailData } from 'features/payment/services/types/bill';
import RefundTicketPrinter from 'features/ticketList/components/VoidTicketPrint/RefundTicketPrinter';
import VoidTicketPrinter from 'features/ticketList/components/VoidTicketPrint/VoidTicketPrinter';
import { useSocketContext } from 'hooks/useSocket';
import { debounce } from 'lodash';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { IResponseDataBody } from 'services/response';
import shopActions from 'services/shop/actions';
import shopApis from 'services/shop/apis';
import { SHOP_DEVICE_TYPE } from 'services/shop/constants';
import shopSelectors from 'services/shop/selectors';
import { ICouponPrintData } from 'services/shop/types/coupon';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { uiLoadingBasicRef } from 'services/UI/LoadingPage/UILoadingRef';
import { useAppDispatch } from 'store/hooks';
import storage from 'utils/sessionStorage';
import { callerIdRef, CallerIdUI } from 'widgets/CallerId';
import CheckInGroupPrinter, { checkInGroupPrinter } from './CheckInGroupPrinter';
import CheckInPrinter from './CheckInPrinter';
import TicketWrapperContext from './TicketWrapperContext';
import WarningCustomerBanPopup from './WarningCustomerBanPopup';
import QuickBookingV2, { quickBookingV2Ref } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2';
import { CallModalStack } from 'widgets/CallerId/CallModalStack';
import usePrintDoubleReceiptWithStaff from './hooks/usePrintDoubleReceiptWithStaff';
import usePrintReceiptWithStaff from './hooks/usePrintReceiptWithStaff';
import usePrintOwnerReceiptWithStaff from './hooks/usePrintOwnerReceiptWithStaff';
import ReceiptStaffPrinter, { ReceiptStaffPrinterRef } from './ReceiptStaffPrinter';
import StaffClockOutPrintManager from 'features/user/pages/clockInClockOut/StaffClockOutPrint';
import ReceiptMultipleCardsV2 from 'features/payment/multipleCard/components/ReceiptMultipleCardsV2';
import ReceiptMultipleCardsV1 from 'features/payment/multipleCard/components/ReceiptMultipleCardsV1';
import PrintBodyV2 from 'features/payment/pages/PrintPage/components/PrintBodyV2';
import PrintBodyV1 from 'features/payment/pages/PrintPage/components/PrintBodyV1';

function ShopWrapperLayout() {
  const { shop_id = '' } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const allSetting = shopSelectors.data.allSetting();
  const setLoadingPage = useSetLoadingPage();
  const divRef = useRef<HTMLDivElement>(null);
  const divOwnerRef = useRef<HTMLDivElement>(null);
  const divStaffReceiptRef = useRef<HTMLDivElement>(null);
  const multiCardReceiptRef = useRef<HTMLDivElement>(null);
  const multiCardOwnerReceiptRef = useRef<HTMLDivElement>(null);
  const receiptStaffPrinter = useRef<ReceiptStaffPrinterRef>(null);

  const socket = useSocketContext();
  const initConnectSocket = debounce(() => socket.connect(), 100);
  useEffect(() => {
    dispatch(shopActions.get.shopInfo.fetch(shop_id));
    const isCheckInRoute = location.pathname?.includes(`store/${shop_id}/check-in`) || location.pathname.includes('ticket/qr-payment/customer-side');
    storage.device_type.set(isCheckInRoute ? SHOP_DEVICE_TYPE.CHECK_IN : SHOP_DEVICE_TYPE.POS);

    initConnectSocket();
    return () => {
      socket.unsubscribeAll();
    };
  }, []);

  const doPrint = useReactToPrint({
    content: () => divRef.current,
  });

  const doPrintDoubleReceipt = useReactToPrint({
    content: () => divOwnerRef.current,
    onAfterPrint: () => doPrint(),
  });

  const doPrintOwnerReceipt = useReactToPrint({
    content: () => divOwnerRef.current,
  });

  const customPrintStaffReceipt = () => {
    receiptStaffPrinter.current?.print();
  };

  const doPrintDoubleReceiptWithStaff = usePrintDoubleReceiptWithStaff({
    customerContent: () => divRef.current,
    ownerContent: () => divOwnerRef.current,
    staffContent: () => divStaffReceiptRef.current,
    customPrintStaffReceipt,
  });

  const doPrintReceiptWithStaff = usePrintReceiptWithStaff({
    customerContent: () => divRef.current,
    ownerContent: () => divOwnerRef.current,
    staffContent: () => divStaffReceiptRef.current,
    customPrintStaffReceipt,
  });

  const doPrintOwnerReceiptWithStaff = usePrintOwnerReceiptWithStaff({
    customerContent: () => divRef.current,
    ownerContent: () => divOwnerRef.current,
    staffContent: () => divStaffReceiptRef.current,
    customPrintStaffReceipt,
  });

  const doPrintStaffReceipt = customPrintStaffReceipt || useReactToPrint({
    content: () => divStaffReceiptRef.current,
  });

  const doMultiCardPrint = useReactToPrint({
    content: () => multiCardReceiptRef.current,
    onAfterPrint: () => {
      dispatch(multiCardActions.setPrintData({ detail: null, coupon: null, cards: [] }));
    }
  });

  const doMultiCardOwnerPrint = useReactToPrint({
    content: () => multiCardOwnerReceiptRef.current,
    onAfterPrint: () => {
      dispatch(multiCardActions.setPrintData({ detail: null, coupon: null, cards: [] }));
    }
  });

  const handlePrintDoubleReceipt = async () => {
    if (allSetting?.enablePrintReceiptStaff) return doPrintDoubleReceiptWithStaff();
    return doPrintDoubleReceipt();
  };

  const handlePrintReceipt = async () => {
    if (allSetting?.enablePrintReceiptStaff) return doPrintReceiptWithStaff();
    return doPrint();
  };

  const handlePrintOwnerWithStaffReceipt = () => {
    if (allSetting?.enablePrintReceiptStaff) return doPrintOwnerReceiptWithStaff();
    return doPrintOwnerReceipt();
  };

  const handlePrint = async (billId: string) => {
    setLoadingPage(true);
    try {
      const response = await Promise.all([
        shopApis.getCouponPrint(billId),
        paymentApis.getBillDetail(billId),
      ]);
      const couponPrint: IResponseDataBody<number> = response[0];
      if (couponPrint.data.data) {
        dispatch(shopActions.get.couponPrint.success(couponPrint.data.data));
      }
      const res: IResponseDataBody<IBillDetailData> = response[1];
      if (res.data.data) {
        dispatch(paymentActions.setPrintBillDetailData(res.data.data));
        setTimeout(() => doPrint(), 100);
      }
    } catch (error) { } finally {
      setLoadingPage(false);
    }
  };

  const handlePrintWithoutCoupon = async (billId: string) => {
    dispatch(shopActions.resetCouponBillPrint());
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<IBillDetailData> = await paymentApis.getBillDetail(billId);
      if (res.data.data) {
        dispatch(paymentActions.setPrintBillDetailData(res.data.data));
        setTimeout(() => doPrint(), 100);
      }
    } catch (error) { } finally {
      setLoadingPage(false);
    }
  };
  const handlePrintNoReceiptWithStaffReceipt = async (billId: string | null, useLoading?: boolean) => {
    if (!billId) return;
    if (useLoading) setLoadingPage(true);
    try {
      const res: IResponseDataBody<IBillDetailData> = await paymentApis.getBillDetail(billId);

      if (res.data.data) {
        dispatch(paymentActions.getPrintBillDetail.success(res.data.data));
        setTimeout(() => doPrintStaffReceipt(), 100);
      }
    } catch (error) { } finally {
      if (useLoading) setLoadingPage(false);
    }
  };

  const handlePrintNoReceipt = async (billId: string | null, useLoading?: boolean) => {
    if (!allSetting?.printNoReceipt) {
      if (allSetting?.enablePrintReceiptStaff) return handlePrintNoReceiptWithStaffReceipt(billId, useLoading);
      return;
    }
    if (!billId) return;
    if (useLoading) setLoadingPage(true);
    try {
      const res: IResponseDataBody<IBillDetailData> = await paymentApis.getBillDetail(billId);

      if (res.data.data) {
        dispatch(paymentActions.getPrintBillDetail.success(res.data.data));
        setTimeout(() => handlePrintOwnerWithStaffReceipt(), 100);
      }
    } catch (error) { } finally {
      if (useLoading) setLoadingPage(false);
    }
  };

  const handleCompletedTicket = async (billId: string | null, useLoading?: boolean) => {
    if (!billId) return;

    if (useLoading) setLoadingPage(true);
    try {
      const response = await Promise.all([
        shopApis.getCouponPrint(billId),
        paymentApis.getBillDetail(billId),
      ]);

      const couponPrint: IResponseDataBody<number> | null = response[0];
      if (couponPrint?.data.data) {
        dispatch(shopActions.get.couponPrint.success(couponPrint?.data.data));
      }

      const res: IResponseDataBody<IBillDetailData> = response[1];

      if (res.data.data) {
        dispatch(paymentActions.getPrintBillDetail.success(res.data.data));
        setTimeout(() => {
          if (allSetting?.doublePrintReceipt) {
            return handlePrintDoubleReceipt();
          } else {
            if (allSetting?.printMultiplePrint) {
              const isCreditCard = res.data.data.payments?.find(o => o.paymentType === PAYMENT_TYPE.CREDIT_CARD);
              if (isCreditCard && !res.data.data.signature) return handlePrintDoubleReceipt();
            }
          }
          handlePrintReceipt();
        }, 100);

        if (allSetting?.confirmSendSmsOrEmail)
          navigate(`/store/${shop_id}/ticket/pay/${billId}/completed`, { replace: true });
        else {
          navigate(`/store/${shop_id}`);
        }
      }
    } catch (error) { } finally {
      if (useLoading) setLoadingPage(false);
    }
  };

  const handlePrintMultiCards = async (billId: string) => {
    if (!billId) return;
    uiLoadingBasicRef.current?.setVisible(true);
    try {
      const response = await Promise.all([
        multiCardApis.getTransactionDetail(billId),
        multiCardApis.getListMultipleCards(billId),
      ]);

      const res: IResponseDataBody<IBillDetailData> = response[0];
      const resCards: IResponseDataBody<IMultiCardResDataItem[]> = response[1];

      dispatch(multiCardActions.setPrintData({ detail: res.data.data || null, coupon: null, cards: resCards.data.data || [] }));

      if (res.data.data) {
        setTimeout(() => doMultiCardPrint(), 100);
      }
    } catch (error) { } finally {
      uiLoadingBasicRef.current?.setVisible(false);
    }
  };

  const handleCompletedMultiCardsPayment = async (billId: string | null) => {
    if (!billId) return;
    uiLoadingBasicRef.current?.setVisible(true);
    try {
      const response = await Promise.all([
        shopApis.getCouponPrint(billId),
        multiCardApis.getTransactionDetail(billId),
        multiCardApis.getListMultipleCards(billId),
      ]);

      const couponPrint: IResponseDataBody<ICouponPrintData> = response[0];
      const res: IResponseDataBody<IBillDetailData> = response[1];
      const resCards: IResponseDataBody<IMultiCardResDataItem[]> = response[2];

      dispatch(paymentActions.getPrintBillDetail.success(res.data.data));
      dispatch(multiCardActions.setPrintData({ detail: res.data.data || null, coupon: couponPrint.data.data || null, cards: resCards.data.data || [] }));

      if (res.data.data) {
        setTimeout(() => doMultiCardPrint(), 100);
        navigate(`/store/${shop_id}`);
      }
    } catch (error) { } finally {
      uiLoadingBasicRef.current?.setVisible(false);
    }
  };

  const handlePrintNoReceiptMultipleCard = async (billId: string | null, useLoading?: boolean) => {
    if (!billId) return;
    if (useLoading) uiLoadingBasicRef.current?.setVisible(true);
    try {
      const response = await Promise.all([
        multiCardApis.getTransactionDetail(billId),
        multiCardApis.getListMultipleCards(billId),
      ]);

      const res: IResponseDataBody<IBillDetailData> = response[0];
      const resCards: IResponseDataBody<IMultiCardResDataItem[]> = response[1];
      dispatch(paymentActions.getPrintBillDetail.success(res.data.data));
      dispatch(multiCardActions.setPrintData({ detail: res.data.data || null, coupon: null, cards: resCards.data.data || [] }));

      if (res.data.data) {
        setTimeout(() => doMultiCardOwnerPrint(), 100);
      }
    } catch (error) { } finally {
      if (useLoading) uiLoadingBasicRef.current?.setVisible(false);
    }
  };

  return (
    <TicketWrapperContext value={{
      print: handlePrint,
      printWithoutCoupon: handlePrintWithoutCoupon,
      completedTicket: handleCompletedTicket,
      printMultiCards: handlePrintMultiCards,
      completedMultiCards: handleCompletedMultiCardsPayment,
      doPrintNoReceipt: handlePrintNoReceipt,
      doPrintNoReceiptMultipleCard: handlePrintNoReceiptMultipleCard,
    }}>
      <Outlet />
      <div style={{ display: 'none' }}>
        {allSetting?.displayPrintReceiptV2 ?
          <PrintBodyV2 billRef={divRef} isPrinter />:
          <PrintBodyV1 billRef={divRef} isPrinter />
        }
      </div>
      <div style={{ display: 'none' }}>
        {allSetting?.displayPrintReceiptV2 ?
          <PrintBodyV2 billRef={divOwnerRef} isPrinter owner />:
          <PrintBodyV1 billRef={divOwnerRef} isPrinter owner />
        }
      </div>
      <div style={{ display: 'none' }}>
        {allSetting?.displayPrintReceiptV2 ?
          <PrintBodyV2 billRef={divStaffReceiptRef} isPrinter staffReceipt />:
          <PrintBodyV1 billRef={divStaffReceiptRef} isPrinter staffReceipt />
        }
      </div>
      <ReceiptStaffPrinter ref={receiptStaffPrinter} />
      <div style={{ display: 'none' }}>
        {allSetting?.displayPrintReceiptV2 ? 
          <ReceiptMultipleCardsV2 billRef={multiCardReceiptRef} isPrinter /> :
          <ReceiptMultipleCardsV1 billRef={multiCardReceiptRef} isPrinter />
        }
      </div>
      <div style={{ display: 'none' }}>
        {allSetting?.displayPrintReceiptV2 ? 
          <ReceiptMultipleCardsV2 billRef={multiCardOwnerReceiptRef} isPrinter owner /> :
          <ReceiptMultipleCardsV1 billRef={multiCardOwnerReceiptRef} isPrinter owner />
        }
      </div>
      <TimeOutRefresh />
      <CheckInPrinter />
      <CheckInGroupPrinter ref={checkInGroupPrinter} />
      <VoidTicketPrinter />
      <RefundTicketPrinter />
      <WarningCustomerBanPopup />
      <ModalConsentForm ref={consentFormRef} />
      <DetailsCustomerModal ref={detailsCustomerModalRef} />
      <CallerIdUI ref={callerIdRef} />
      <CallModalStack />
      <QuickBookingV2 ref={quickBookingV2Ref} />
      <StaffClockOutPrintManager.Portal />
    </TicketWrapperContext>
  );
}

export default ShopWrapperLayout;

const TimeOutRefresh = () => {
  const handleReload = () => {
    const now = moment();
    const startTime = moment().set({ hour: 9, minute: 0, second: 0, });
    const endTime = moment().set({ hour: 21, minute: 0, second: 0, });

    if (now.isBetween(startTime, endTime)) return;

    if (storage.device_type.get() === SHOP_DEVICE_TYPE.CHECK_IN) {
      window.location.href = `${location.origin}/store/${storage.shop_id.get()}/check-in/sign-in`;
    } else {
      window.location.href = `${location.origin}/store/${storage.shop_id.get()}`;
    }
  };

  useEffect(() => {
    const time = setInterval(() => {
      handleReload();
    }, 7200000);
    return () => {
      clearInterval(time);
    };
  }, []);

  return null;
};